import { useState, FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { differenceInCalendarDays } from "date-fns";
import { Box, IconButton, Avatar, Stack, Link, ListItem } from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { theme } from "@zesty-io/material";
import GlobalMenu from "../global-menu";
import { Favicon } from "../Favicon";
import zestyLogo from "../../../../public/images/zestyLogo.svg";
import zestyLogoOnly from "../../../../public/images/zestyLogoOnly.svg";
import zestyLogoOnlyGrey from "../../../../public/images/zestyLogoOnlyGrey.svg";
import contentOneLogo from "../../../../public/images/contentOneLogo.webp";
import contentOneLogoOnly from "../../../../public/images/contentOneLogoOnly.webp";
import githubLogoSmall from "../../../../public/images/githubLogoSmall.svg";
import InviteMembersModal from "../InviteMembersModal";
import { User } from "../../services/types";
import { useGetInstanceQuery } from "../../services/accounts";
import { AppState } from "../../store/types";
import { InstanceMenu } from "./components/InstanceMenu";
import { actions } from "../../store/ui";
import { OnboardingCall } from "./components/OnboardingCall";
import { GlobalAccountMenu } from "../GlobalAccountMenu";
import { GlobalDocsMenu } from "../GlobalDocsMenu";
import { isContentOne } from "../../../utility/isContentOne";
interface GlobalSidebarProps {
  openNav: boolean;
  onClick: () => void;
}
const GlobalSidebar: FC<GlobalSidebarProps> = ({
  onClick,
  openNav
}) => {
  const {
    t
  } = useTranslation();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const user: User = useSelector((state: AppState) => state.user);
  const {
    data: instance
  } = useGetInstanceQuery();
  const accountMenuBtn = useRef<HTMLDivElement | null>(null);
  const [showDocsMenu, setShowDocsMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const ui = useSelector((state: AppState) => state.ui);
  const dispatch = useDispatch();
  const is15DaysFromCreation = !!instance?.createdAt && differenceInCalendarDays(new Date(), new Date(instance.createdAt)) <= 15;
  const logo = isContentOne() ? contentOneLogo : zestyLogo;
  const logoOnly = isContentOne() ? contentOneLogoOnly : zestyLogoOnly;
  const logoOnlyGrey = isContentOne() ? contentOneLogoOnly : zestyLogoOnlyGrey;
  return <>
      <Box component="aside" position="relative" boxSizing="border-box" sx={{
      backgroundColor: "grey.900",
      borderRight: "1px solid",
      borderColor: "grey.800"
    }} data-sentry-element="Box" data-sentry-source-file="GlobalSidebar.tsx">
        <ListItem sx={{
        height: 36,
        p: 0,
        mt: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }} data-sentry-element="ListItem" data-sentry-source-file="GlobalSidebar.tsx">
          <IconButton data-cy="CollapseGlobalSideBar" onClick={onClick} size="large" sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: 0,
          "&:hover": {
            backgroundColor: "rgba(255, 93, 10, 0.08)"
          }
        }} data-sentry-element="IconButton" data-sentry-source-file="GlobalSidebar.tsx">
            <MenuRoundedIcon sx={{
            color: "grey.400"
          }} data-sentry-element="MenuRoundedIcon" data-sentry-source-file="GlobalSidebar.tsx" />
          </IconButton>
          {openNav && <Box component="img" src={logoOnlyGrey} alt="Zesty Logo" width={20} height={20} mr={1.5} />}
        </ListItem>

        <InstanceMenu openNav={openNav} data-sentry-element="InstanceMenu" data-sentry-source-file="GlobalSidebar.tsx" />
        <GlobalMenu data-sentry-element="GlobalMenu" data-sentry-source-file="GlobalSidebar.tsx" />

        {is15DaysFromCreation && openNav && <OnboardingCall />}
        {/* Bottom bar */}
        <Box position="absolute" bottom={0} left={0} right={0} data-sentry-element="Box" data-sentry-source-file="GlobalSidebar.tsx">
          <Stack direction={openNav ? "row" : "column"} alignItems="center" justifyContent="space-between" height={openNav ? 24 : "inherit"} px={1.5} mb={openNav ? 1.5 : 0} pt={openNav ? 0 : 1.5} gap={openNav ? 1.5 : 1} borderTop={openNav ? "none" : "1px solid"} sx={{
          borderColor: "grey.800"
        }} data-sentry-element="Stack" data-sentry-source-file="GlobalSidebar.tsx">
            <img src={openNav ? logo : logoOnly} style={{
            objectFit: "contain"
          }} alt="Zesty Logo" width={openNav ? 84 : 20} height={openNav ? 24 : 20} />
            <Stack direction="row" gap={0.5} alignItems="end" data-sentry-element="Stack" data-sentry-source-file="GlobalSidebar.tsx">
              {openNav && <Box component="img" src={githubLogoSmall} alt="Github Logo" width={12} height={12} />}
              <Link fontFamily="Roboto Mono" fontSize={10} letterSpacing={0.15} lineHeight="10px" color="grey.500" underline="none" href={`https://github.com/zesty-io/manager-ui/commit/${CONFIG?.build?.data?.gitCommit}`} target="_blank" rel="noopener" width={openNav ? "inherit" : 32} textAlign="center" title={t("shell.viewSourceCommit")} sx={{
              wordWrap: "break-word"
            }} data-sentry-element="Link" data-sentry-source-file="GlobalSidebar.tsx">
                #{CONFIG?.build?.data?.gitCommit}
              </Link>
            </Stack>
          </Stack>

          <Stack width="inherit" justifyContent="space-between" overflow="hidden" borderTop={openNav ? "1px solid" : "none"} alignItems="center" flexDirection={openNav ? "row" : "column-reverse"} pt={openNav ? 1.25 : 1} pb={openNav ? 1.25 : 1.5} px={openNav ? 1.5 : 1} gap={openNav ? 0 : 1} sx={{
          borderColor: "grey.800"
        }} data-sentry-element="Stack" data-sentry-source-file="GlobalSidebar.tsx">
            <Stack ref={accountMenuBtn} data-cy="globalAccountAvatar" direction="row" alignItems="center" width={openNav ? 49 : 32} onClick={() => {
            setShowAccountMenu(true);
          }} fontSize={16} sx={{
            "&:hover": {
              cursor: "pointer"
            }
          }} data-sentry-element="Stack" data-sentry-source-file="GlobalSidebar.tsx">
              <Avatar alt={`${user.firstName} ${user.lastName} Avatar`} src={`https://www.gravatar.com/avatar/${user.emailHash}.jpg?&s=40`} sx={{
              height: 32,
              width: 32
            }} data-sentry-element="Avatar" data-sentry-source-file="GlobalSidebar.tsx" />
              {openNav && <ArrowDropDownRoundedIcon fontSize="small" sx={{
              color: "grey.500"
            }} />}
            </Stack>
            <Box sx={{
            display: "flex",
            flexDirection: openNav ? "row" : "column",
            alignItems: "center"
          }} data-sentry-element="Box" data-sentry-source-file="GlobalSidebar.tsx">
              <IconButton data-cy="InviteUser" onClick={() => setShowInviteModal(true)} sx={{
              backgroundColor: "grey.800",
              height: "26px",
              width: "32px",
              borderRadius: "4px",
              mr: openNav ? 1 : 0
            }} data-sentry-element="IconButton" data-sentry-source-file="GlobalSidebar.tsx">
                <GroupAddIcon fontSize="small" sx={{
                color: "grey.500"
              }} data-sentry-element="GroupAddIcon" data-sentry-source-file="GlobalSidebar.tsx" />
              </IconButton>
              <IconButton data-cy="ReadDocs" onClick={() => {
              setShowDocsMenu(true);
            }} sx={{
              backgroundColor: "grey.800",
              width: "32px",
              mt: openNav ? 0 : 1,
              height: "26px",
              borderRadius: "4px"
            }} data-sentry-element="IconButton" data-sentry-source-file="GlobalSidebar.tsx">
                <MenuBookIcon fontSize="small" sx={{
                color: "grey.500"
              }} data-sentry-element="MenuBookIcon" data-sentry-source-file="GlobalSidebar.tsx" />
              </IconButton>
            </Box>
            {showInviteModal && <InviteMembersModal onClose={() => setShowInviteModal(false)} />}

            <GlobalAccountMenu open={showAccountMenu} anchorEl={accountMenuBtn?.current} onClose={() => {
            setShowAccountMenu(false);
          }} onShowDocsMenu={() => {
            setShowAccountMenu(false);
            setShowDocsMenu(true);
          }} data-sentry-element="GlobalAccountMenu" data-sentry-source-file="GlobalSidebar.tsx" />

            <GlobalDocsMenu open={showDocsMenu} anchorEl={accountMenuBtn?.current} onClose={() => {
            setShowDocsMenu(false);
          }} data-sentry-element="GlobalDocsMenu" data-sentry-source-file="GlobalSidebar.tsx" />
          </Stack>
        </Box>
      </Box>

      {ui.isUpdateFaviconModalOpen && <Favicon
    // @ts-ignore not a typescript file
    onCloseFaviconModal={() => dispatch(actions.toggleUpdateFaviconModal(false))} />}
    </>;
};
export default GlobalSidebar;